import { __rest } from "tslib";
import ArrowDropDown from '@mui/icons-material/ArrowDropDown';
import ArrowRight from '@mui/icons-material/ArrowRight';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/GridLegacy';
import Grow from '@mui/material/Grow';
import ListSubheader from '@mui/material/ListSubheader';
import MUIMenu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import MenuList from '@mui/material/MenuList';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import classnames from 'classnames';
import React, { useState, forwardRef, Children, Fragment } from 'react';
import { withStyles } from '../../styles/tools';
import { withTrace } from '../../utils';
import { Spinner } from '../Spinner';
import { styles } from './Menu.styles';
export const MenuItemWithIconBase = ({ IconElement, label, loading = false, applyTrace, }) => (React.createElement(Grid, Object.assign({ container: true, wrap: "nowrap", alignItems: "center", spacing: 2 }, applyTrace()),
    React.createElement(Grid, Object.assign({ item: true }, applyTrace('icon')), loading ? (React.createElement(Spinner, Object.assign({ size: 24, position: "relative", color: "inherit" }, applyTrace('loading')))) : (React.createElement(IconElement, null))),
    React.createElement(Grid, Object.assign({ item: true }, applyTrace('label')), label)));
export const MenuItemWithIcon = withTrace('item')(MenuItemWithIconBase);
export function MenuBase(props) {
    const { open, options, optionGroups, classes, onClose, popoverClasses, applyTrace, customHeight } = props, menuProps = __rest(props, ["open", "options", "optionGroups", "classes", "onClose", "popoverClasses", "applyTrace", "customHeight"]);
    const optionGroupsCollapsedDefault = {};
    if (optionGroups !== undefined) {
        optionGroups.forEach((optionGroup) => {
            if (optionGroup.key !== undefined) {
                optionGroupsCollapsedDefault[optionGroup.key] =
                    optionGroup.collapsedDefault === true;
            }
        });
    }
    const [optionGroupsCollapsed, setOptionGroupsCollapsed] = useState(optionGroupsCollapsedDefault);
    return (React.createElement(MUIMenu, Object.assign({ open: open, classes: { paper: classes.paper }, PopoverClasses: popoverClasses, MenuListProps: { disablePadding: true }, onClick: (event) => {
            onClose();
            event.stopPropagation();
        }, onClose: (event) => {
            onClose();
            event.stopPropagation();
        } }, menuProps, { disableScrollLock: true, sx: {
            '& .MuiPaper-root': {
                maxHeight: customHeight !== null && customHeight !== void 0 ? customHeight : 'auto',
            },
        } }, applyTrace('mui-menu')), optionGroups ? (optionGroups.map((optGr, index) => {
        var _a;
        return (React.createElement("span", { key: index, className: classes.menuGroupWrapper },
            index > 0 ? (React.createElement(Divider, { variant: "middle", className: classes.divider })) : null,
            optGr.collapsible ? (React.createElement(Grid, { "automation-id": `option-group-header-${(_a = optGr.key) !== null && _a !== void 0 ? _a : optGr.label}`, container: true, direction: "row", style: { cursor: 'pointer' }, onClick: (event) => {
                    const nextOptionGroupsCollapsed = Object.assign({}, optionGroupsCollapsed);
                    if (optGr.key !== undefined) {
                        nextOptionGroupsCollapsed[optGr.key] =
                            !nextOptionGroupsCollapsed[optGr.key];
                    }
                    setOptionGroupsCollapsed(nextOptionGroupsCollapsed);
                    event.stopPropagation();
                } },
                React.createElement(Grid, null,
                    React.createElement(ListSubheader, { className: classes.menuLabel }, optGr.label)),
                React.createElement(Grid, { item: true }, optGr.key !== undefined && optionGroupsCollapsed[optGr.key] ? (React.createElement(ArrowRight, { className: classes.collapsibleOptionGroupArrow })) : (React.createElement(ArrowDropDown, { className: classes.collapsibleOptionGroupArrow }))))) : (React.createElement(ListSubheader, { className: classes.menuLabel }, optGr.label)),
            (!optGr.collapsible ||
                optGr.key === undefined ||
                !optionGroupsCollapsed[optGr.key]) &&
                optGr.optionIds.map((optKey) => options
                    .filter((opt) => opt.key === optKey)
                    .map((option, index) => (React.createElement(MenuOptionsList, Object.assign({ key: index }, props, { options: [option] })))))));
    })) : (React.createElement(MenuOptionsList, Object.assign({}, props)))));
}
export const MenuOptionsList = forwardRef((props, ref) => {
    const { options, onClose, classes, applyTrace, children, actionButton } = props;
    return (React.createElement(React.Fragment, null,
        actionButton,
        options.map(({ key, action, isDisabled = () => false, ItemComponent, selected, itemProps, divideAfter = false, dividerClassName, }) => (React.createElement(Fragment, { key: key },
            React.createElement(MenuItem, Object.assign({ ref: ref, className: classes.menuItem, disabled: isDisabled(), selected: selected, onClick: (event) => {
                    onClose();
                    if (action) {
                        action(key);
                    }
                    event.stopPropagation();
                } }, applyTrace(`menu-item-${key}`)),
                React.createElement(ItemComponent, Object.assign({}, itemProps))),
            divideAfter ? (React.createElement(Divider, Object.assign({ className: dividerClassName }, applyTrace(`divider-after-${key}`)))) : null))),
        Children.map(children, (child, index) => (React.createElement("span", { key: index }, child)))));
});
export function AttachedMenuBase(props) {
    const { open, classes, onClose, popoverClasses, applyTrace, anchorEl, popperClassName, popperPlacement = 'bottom-start' } = props, menuProps = __rest(props, ["open", "classes", "onClose", "popoverClasses", "applyTrace", "anchorEl", "popperClassName", "popperPlacement"]);
    const paperClasses = popoverClasses && popoverClasses.paper
        ? classnames(classes.paper, popoverClasses.paper)
        : classes.paper;
    const popper = classnames(popperClassName, { [classes.popper]: !anchorEl });
    return (React.createElement(Popper, { className: popper, anchorEl: anchorEl, open: open, placement: popperPlacement, modifiers: [
            {
                name: 'flip',
                enabled: false,
            },
            {
                name: 'offset',
                enabled: true,
                options: {
                    offset: [0, 0],
                },
            },
            {
                name: 'preventOverflow',
                enabled: true,
                options: {
                    escapeWithReference: true,
                },
            },
        ], transition: true, disablePortal: true }, ({ TransitionProps }) => (React.createElement(ClickAwayListener, { mouseEvent: "onMouseUp", touchEvent: "onTouchEnd", onClickAway: (event) => {
            if (anchorEl && anchorEl.contains(event.target)) {
                return;
            }
            if (event.currentTarget !== anchorEl) {
                onClose();
                event.stopPropagation();
            }
        } },
        React.createElement(Grow, Object.assign({}, TransitionProps),
            React.createElement(Paper, { className: paperClasses },
                React.createElement(MenuList, Object.assign({ disablePadding: true }, menuProps, applyTrace('mui-menu')),
                    React.createElement(MenuOptionsList, Object.assign({}, props)))))))));
}
const traced = withTrace('menu')(MenuBase);
const attachedTraced = withTrace('menu')(AttachedMenuBase);
export const Menu = withStyles(styles)(traced);
export const AttachedMenu = withStyles(styles)(attachedTraced);
export default Menu;
//# sourceMappingURL=Menu.js.map